import React, { useState } from 'react';
import {
  AIAssistantDesription,
  AIAssistantCardWrapper,
  AIAssistantName,
  AIAssistantTitle,
} from './styles';
import { VideoItemThumbnail } from 'app/pages/library/videos/components';
import { ModalVideoListVideo } from 'lib/components';
import { VideoEncodingStatus } from 'lib/const/VideoEncoding';
import { DESCRIPTION_FALLBACK } from './constants';
import { Button } from 'react-covideo-common';
import { Flex } from 'lib/components/styles/layout';
import { IoCheckmark } from 'react-icons/io5';
import { IUpsertCustomerAvatarParams } from 'lib/api/avatars/customerAvatars/useEditCustomerAvatarMutation';
import { IAIAssistantResponse } from 'lib/api/avatars/customerAvatars/useQueryCustomerAvatars';
import { useTheme } from 'styled-components/macro';
import { InlineTextEdit } from 'lib/components/InlineTextEdit';

interface AIAssistantCardProps {
  aiAssistant: IAIAssistantResponse;
  onEditCustomerAvatar: (params: IUpsertCustomerAvatarParams) => void;
  isLoading: boolean;
}

export const AIAssistantCard = ({
  aiAssistant,
  onEditCustomerAvatar,
  isLoading,
}: AIAssistantCardProps) => {
  const theme = useTheme();

  const {
    avatarId,
    previewVideoUrl,
    avatarName,
    default: isDefault,
    previewImageUrl,
  } = aiAssistant;

  const isDefaultAIAssistant = !!isDefault;

  const [videoSource, setVideoSource] = useState('');

  const handleChangeDefaultAvatarClick = () => {
    if (isDefaultAIAssistant) {
      return undefined;
    }
    onEditCustomerAvatar({ ...aiAssistant, default: 1 });
  };

  const handleSave =
    (key: keyof IUpsertCustomerAvatarParams) => (value: string) => {
      onEditCustomerAvatar({ ...aiAssistant, [key]: value });
    };

  const description = aiAssistant.description || DESCRIPTION_FALLBACK;
  const title = aiAssistant.title;

  const buttonText = isDefaultAIAssistant
    ? 'Default Avatar'
    : 'Set As Default Avatar';
  const buttonVariant = isDefaultAIAssistant ? 'passive' : 'white';
  const buttonIcon = isDefaultAIAssistant ? (
    <IoCheckmark size={20} color={theme.colors.white[80]} />
  ) : undefined;

  return (
    <AIAssistantCardWrapper default={isDefaultAIAssistant}>
      <Flex flexDirection='column' gap='20px' maxWidth='270px'>
        <VideoItemThumbnail
          handleClick={() => {
            setVideoSource(previewVideoUrl);
          }}
          videoId={avatarId}
          thumbnail={previewImageUrl}
          duration={0}
          processing={VideoEncodingStatus.READY}
          thumbnailHeight='170px'
        />

        <Flex flexDirection='column' gap='17px'>
          <Flex flexDirection='column' gap='6px'>
            <InlineTextEdit
              value={avatarName}
              onSave={handleSave('avatarName')}
              viewComponent={AIAssistantName}
              disabled={isLoading}
            />
            <InlineTextEdit
              value={title}
              onSave={handleSave('title')}
              viewComponent={AIAssistantTitle}
              disabled={isLoading}
            />
          </Flex>
          <InlineTextEdit
            value={description || DESCRIPTION_FALLBACK}
            onSave={handleSave('description')}
            viewComponent={AIAssistantDesription}
            disabled={true}
          />
        </Flex>
        <Button
          variant={buttonVariant}
          size='small'
          text={buttonText}
          icon={buttonIcon}
          disabled={isLoading}
          onClick={handleChangeDefaultAvatarClick}
          extendStyles={{
            text: {
              fontWeight: 400,
            },
          }}
        />
      </Flex>
      {!!videoSource && (
        <ModalVideoListVideo
          videoId={''}
          source={videoSource}
          handleModalClose={() => {
            setVideoSource('');
          }}
          hideContext={true}
        />
      )}
    </AIAssistantCardWrapper>
  );
};
